import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, pollJob, rateLimitMessage } from "@/lib/api";
import { Search, Sparkles, ArrowRight, Building2, FileCode2 } from "lucide-react";
import { Loader, LoaderStages } from "@/components/Loader";
import { RegionBadge } from "@/components/RegionBadge";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
};

const SUGGESTIONS = ["SCHOTT", "AVX", "ASML", "SAP", "Siemens", "Spotify", "Adyen", "ARM", "Bosch", "Inditex", "BMW", "LVMH", "Stripe", "Nvidia"];

export default function Research() {
    const nav = useNavigate();
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [saving, setSaving] = useState(false);
    const [patterns, setPatterns] = useState([]);
    const [patternId, setPatternId] = useState("");

    useEffect(() => { api.get("/patterns").then(({ data }) => setPatterns(data)).catch(() => {}); }, []);

    const runResearch = async (e) => {
        e?.preventDefault();
        if (!query.trim()) return;
        setLoading(true); setError(""); setResult(null);
        try {
            const { data: enqueued } = await api.post("/research", { firm_name: query.trim(), pattern_id: patternId || null });
            const result = await pollJob(enqueued.job_id);
            setResult(result);
        } catch (err) {
            const rl = rateLimitMessage(err);
            const d = err?.response?.data?.detail;
            let msg;
            if (rl) msg = rl;
            else if (typeof d === "string") msg = d;
            else if (err.jobError) msg = err.message;
            else if (err.code === "ECONNABORTED") msg = "Request timed out — the AI took too long. Please try again.";
            else if (err.message?.includes("Network Error")) msg = "Network error — couldn't reach the server. Check your connection and try again.";
            else msg = err.message || "Research failed. Try again.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const saveAndOpen = async () => {
        if (!result) return;
        setSaving(true);
        try {
            const { data } = await api.post("/firms", result);
            nav(`/app/firms/${data.id}`);
        } catch {
            setError("Failed to save firm");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-8" data-testid="research-page">
            <header className="mb-8 border-b border-zinc-800 pb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Research</div>
                <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Investigate a firm</h1>
                <p className="text-sm text-zinc-500 mt-2">Type a company name — Claude Sonnet 4.5 builds the dossier.</p>
            </header>

            <form onSubmit={runResearch} className="mb-6">
                <div className="flex items-stretch border-2 border-zinc-800 focus-within:border-amber-500 bg-zinc-950 transition-colors">
                    <div className="flex items-center pl-4 pr-3 border-r border-zinc-800">
                        <Search className="w-5 h-5 text-zinc-500" />
                    </div>
                    <input
                        autoFocus
                        data-testid="firm-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="e.g. Nvidia, Stripe, Patagonia..."
                        className="flex-1 bg-transparent px-4 py-5 text-lg text-zinc-100 placeholder:text-zinc-600 focus:outline-none font-mono"
                    />
                    <button type="submit" disabled={loading || !query.trim()} data-testid="run-research-btn" className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-wider px-6 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" /> Research
                    </button>
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">Quick try:</span>
                    {SUGGESTIONS.map((s) => (
                        <button key={s} type="button" data-testid={`suggest-${s}`} onClick={() => setQuery(s)} className="border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs text-zinc-400 px-2.5 py-1 transition-colors">
                            {s}
                        </button>
                    ))}
                    <div className="flex items-center gap-2 ml-auto">
                        <FileCode2 className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">Pattern:</span>
                        <select
                            data-testid="research-pattern-select"
                            value={patternId}
                            onChange={(e) => setPatternId(e.target.value)}
                            className="bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 py-1 focus:outline-none focus:border-amber-500 font-mono"
                        >
                            <option value="">None (default)</option>
                            {patterns.map((p) => <option key={p.id} value={p.id}>{`${p.name} · ${p.fields.length}f`}</option>)}
                        </select>
                    </div>
                </div>
            </form>

            {error && (
                <div data-testid="research-error" className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono mb-6">
                    {error}
                </div>
            )}

            {loading && (
                <div className="border border-zinc-800 bg-zinc-950 p-12">
                    <Loader label={`Building dossier for ${query}`} stages={LoaderStages.research} />
                </div>
            )}

            {result && !loading && (
                <div className="border border-zinc-800 bg-zinc-950 animate-fade-in" data-testid="research-result">
                    <div className="border-b border-zinc-800 p-6 flex items-start justify-between gap-6">
                        <div className="min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">Dossier · Claude Sonnet 4.5</div>
                                {result.region && <RegionBadge region={result.region} />}
                            </div>
                            <h2 className="font-heading font-black tracking-tighter text-3xl text-zinc-50 mb-2">{result.name}</h2>
                            <div className="flex items-center gap-4 font-mono text-xs text-zinc-500">
                                <span>{result.industry || "—"}</span>
                                <span>·</span>
                                <span>{result.headquarters || "—"}</span>
                                <span>·</span>
                                <span>Founded {result.founded_year || "—"}</span>
                            </div>
                        </div>
                        <button onClick={saveAndOpen} disabled={saving} data-testid="save-firm-btn" className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5 flex items-center gap-2 shrink-0">
                            {saving ? "Saving…" : <>Save to library <ArrowRight className="w-3.5 h-3.5" /></>}
                        </button>
                    </div>

                    <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Overview</div>
                            <p className="text-sm text-zinc-300 leading-relaxed">{result.overview}</p>

                            {result.custom_fields && Object.keys(result.custom_fields).length > 0 && (
                                <div className="mt-6">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-2">Pattern fields</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {Object.entries(result.custom_fields).map(([k, v]) => (
                                            <div key={k} className="border border-zinc-800 p-3 bg-black/40">
                                                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{k}</div>
                                                <div className="text-sm text-zinc-200">{Array.isArray(v) ? v.join(", ") : String(v)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Recent intel</div>
                            <ul className="space-y-2">
                                {(result.recent_news || []).map((n, i) => (
                                    <li key={i} className="text-sm text-zinc-300 border-l-2 border-zinc-800 pl-3">{n}</li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Competitors</div>
                            <div className="flex flex-wrap gap-1.5 mb-6">
                                {(result.competitors || []).map((c) => (
                                    <span key={c} className="border border-zinc-700 bg-zinc-900/50 text-zinc-300 text-xs px-2 py-0.5">{c}</span>
                                ))}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Tags</div>
                            <div className="flex flex-wrap gap-1.5">
                                {(result.tags || []).map((t) => (
                                    <span key={t} className="border border-amber-500/40 bg-amber-500/10 text-amber-400 text-xs px-2 py-0.5">{t}</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-zinc-800 p-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Products · Money Loop</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {(result.products || []).map((p) => (
                                <div key={p.id || p.name} className="border border-zinc-800 p-4 bg-black/40">
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="font-heading font-bold text-zinc-50 text-base">{p.name}</div>
                                        <div className={`font-mono text-xs ${p.growth_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                                            {p.growth_pct >= 0 ? "+" : ""}{p.growth_pct?.toFixed(1)}%
                                        </div>
                                    </div>
                                    <p className="text-xs text-zinc-500 mb-3 leading-relaxed">{p.description}</p>
                                    <div className="grid grid-cols-3 gap-2 font-mono text-[11px]">
                                        <div>
                                            <div className="text-zinc-600 uppercase tracking-wider text-[9px]">Revenue</div>
                                            <div className="text-amber-500">{fmtMoney(p.annual_revenue_usd)}</div>
                                        </div>
                                        <div>
                                            <div className="text-zinc-600 uppercase tracking-wider text-[9px]">Share</div>
                                            <div className="text-zinc-200">{p.revenue_share_pct?.toFixed(0)}%</div>
                                        </div>
                                        <div>
                                            <div className="text-zinc-600 uppercase tracking-wider text-[9px]">Pricing</div>
                                            <div className="text-zinc-300 truncate">{p.pricing || "—"}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {!result && !loading && !error && (
                <div className="border border-dashed border-zinc-800 p-12 text-center">
                    <Building2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">Awaiting query</div>
                </div>
            )}
        </div>
    );
}
